import { SettingsBuilder } from '../../components/SettingsBuilder'
import { getScheduleValues } from '../../model/settings'
import { Flex, Box, Text, useToast } from '@chakra-ui/core'
import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from 'constants/interfaces'

import ChooseAudience from './ChooseAudience'
import ChooseAuditors from './ChooseAuditors'
import TaskTypeSelection from './TaskTypeSelection'
import { TimeFramePreview } from './TimeFramePreview'
import { Controller, FormContext, useForm } from 'react-hook-form'
import { StepType, TaskFormData, TaskTimeFrame, useFormStepContext } from '../../FormStepContext'
import { IndividualResultsSwitch } from './IndividualResultsSwitch'
import { SchedulePushSwitch } from './SchedulePushSwitch'
import { DisableGalleryUploadSwitch } from './DisableGalleryUploadSwitch'
import { FeedbackLimitSwitch } from './FeedbackLimitSwitch'
import { AllowCompletionAfterDeadlineSwitch } from './AllowCompletionAfterDeadlineSwitch'
import { ChooseWatchers } from './ChooseWatchers'
import { Settings } from 'lucide-react'
import useLocaleText from 'components/useLocaleText'
import { getTaskSettingsDefaults } from '../../model/taskSettingsDefaults'
import PrioritySelection from './PrioritySelection'

const TaskSettingStep = ({
  dualView = false,
  headerSlot,
}: {
  dualView?: boolean
  headerSlot?: HTMLDivElement | null
}) => {
  const { setCurrentFormSubmit, updateFormData, formData, submitAllForms, isVmTask, isEdit } = useFormStepContext()

  const error = useSelector((state: RootState) => state.tasks_v2.error)
  const toast = useToast()
  const theme = useSelector((state: RootState) => state.general.theme)
  const uid = useSelector((state: RootState) => state.auth.uid)
  const t_advancedOptions = useLocaleText('advanced_options')
  const save = useLocaleText('save')

  const defaultValues = getTaskSettingsDefaults(formData, isVmTask, uid)

  const methods = useForm<TaskFormData['TASK_SETTINGS'] & Partial<Omit<TaskTimeFrame, 'deadline'>>>({
    defaultValues: dualView
      ? {
          frequency_time: '',
          monthly_range: [],
          is_recurring: false,
          days_of_week: null,
          recurrence_frequency: null,
          review_to_ts: null,
          ...defaultValues,
          ...formData[StepType.TIME_FRAME_SELECTION],
          deadline: formData[StepType.TIME_FRAME_SELECTION]?.deadline ?? defaultValues.deadline,
        }
      : defaultValues,
    mode: dualView ? 'onChange' : 'onSubmit',
  })

  const { handleSubmit } = methods

  // Watch all form values — subscribes to changes and re-renders on every change
  const watchedValues = methods.watch()

  useEffect(() => {
    setCurrentFormSubmit(() =>
      handleSubmit((data) => {
        updateFormData(StepType.TASK_SETTINGS, data)
        if (dualView) updateFormData(StepType.TIME_FRAME_SELECTION, getScheduleValues(data))
        submitAllForms()
      })
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Show toast when error appears
  useEffect(() => {
    if (error) {
      toast({
        title: 'Task Update Failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    }
  }, [error, toast])

  // Persist form values to context on every change so navigating between
  // steps (e.g. opening TimeFrameSelection and coming back) never resets values
  useEffect(() => {
    updateFormData(StepType.TASK_SETTINGS, watchedValues as any)
    if (dualView) updateFormData(StepType.TIME_FRAME_SELECTION, getScheduleValues(watchedValues))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(watchedValues)])

  return (
    <FormContext {...methods}>
      {dualView ? (
        <SettingsBuilder headerSlot={headerSlot} actionLabel={isEdit ? save : undefined} />
      ) : (
        <Flex flexDir="column" style={{ rowGap: '40px' }}>
          <ChooseAudience />
          {isVmTask && <ChooseAuditors />}
          <ChooseWatchers />
          <TaskTypeSelection />
          <TimeFramePreview />
          <PrioritySelection />
          <Box mt={2}>
            <Flex alignItems="center" style={{ columnGap: '20px' }}>
              <Settings size={22} color={theme?.elementsColor} />
              <Text fontWeight="bold" fontSize="16px">
                {t_advancedOptions}
              </Text>
            </Flex>

            <Box py="10px">
              <Flex flexDir="column" style={{ rowGap: '16px' }}>
                <IndividualResultsSwitch number={1} />
                <SchedulePushSwitch number={2} />
                <DisableGalleryUploadSwitch number={3} />
                {isVmTask && <FeedbackLimitSwitch number={4} />}
                <AllowCompletionAfterDeadlineSwitch number={isVmTask ? 5 : 4} />
              </Flex>
            </Box>
          </Box>
        </Flex>
      )}
      <Controller as={<Box />} name="type" control={methods.control} />
    </FormContext>
  )
}

export default TaskSettingStep
